import React from 'react';
import I18n from 'i18n-js';
import HeaderDropdown from '@reverbdotcom/commons/src/components/header_dropdown';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import gql from 'graphql-tag';
import { RCIcon } from '@reverbdotcom/commons/src/rc/RCIcon';
import CartSvg from '@reverbdotcom/commons/design-system/icons/core/cart.svg?component';

import ReverbHeaderCartItem, { ICartItem } from './reverb_header_cart_item';
import PriceDisplay from '@reverbdotcom/commons/src/components/price_display';
import UrlHelpers from '../../shared/url_helpers';
import { V3 } from '../../api_request';
import { ReverbHeaderCartBadgeData, core_apimessages_Money } from '../../../schemas/rql';

const MAXIMUM_VISIBLE_CART_ITEMS = 3;

interface IProps {
  me: ReverbHeaderCartBadgeData.Fragment;

  activeDropdownKey: string | null;
  setActiveDropdownKey: (string) => void;
}

interface ICart {
  count: number;
  cart_items: ICartItem[];
}

export const ReverbHeaderCartBadgeFragment = gql`
  fragment ReverbHeaderCartBadgeData on rql_Me {
    reverbCredits {
      display
      currency
    }
  }
`;

interface IReverbCreditsCalloutProps {
  reverbCredits: core_apimessages_Money[] | null;
}

function ReverbCreditsCallout({ reverbCredits }: IReverbCreditsCalloutProps) {
  if (!reverbCredits?.length) { return null; }

  return (
    <>
      {reverbCredits.map(({ currency, display }) => (
        <li className="bdb-1 bd-primary" key={currency}>
          <div className="reverb-header__cart__credits-balance">
            <span className="mr-space">
              {I18n.t('discovery.reverbHeader.cart.credits')}
            </span>
            <div className="weight-bold color-green">
              <PriceDisplay
                display={display}
                currency={currency}
              />
            </div>
          </div>
        </li>
      ))}
    </>
  );
}

function CartBadgeDropdownTrigger({ countForDataTag }) {
  return (
    <CoreLink to={UrlHelpers.cartPath}>
      <div
        className="reverb-header__nav__link"
      >
        <div
          className="reverb-header__nav__link__icon"
          data-notification-count={countForDataTag}
        >
          <RCIcon svgComponent={CartSvg} />
        </div>
        <div className="reverb-header__nav__link__label">
          {I18n.t('discovery.reverbHeader.cart.badge')}
        </div>
        <span className="reverb-header__count-badge">
          {countForDataTag && I18n.t('discovery.reverbHeader.cart.badgeCount', { count: countForDataTag })}
        </span>
      </div>
    </CoreLink>
  );
}

function ReverbHeaderCartBadge({ activeDropdownKey, setActiveDropdownKey, me }: IProps) {
  const [cartState, setCartState] = React.useState<ICart | null>();
  const { cart_items: cartItems = [], count } = cartState || {};

  const reverbCredits = me?.reverbCredits;

  React.useEffect(() => {
    V3.get(UrlHelpers.apiCartPath).then((response) => {
      setCartState(response);
    });
  }, []);

  const hasCartItems = count > 0;
  const countForDataTag = hasCartItems ? count : null;

  function handleDropdownOpen(dropdownKey) {
    if (!hasCartItems) {
      return;
    }

    setActiveDropdownKey(dropdownKey);
  }

  function visibleCartItems() {
    if (!cartState) { return []; }

    return cartItems.slice(0, MAXIMUM_VISIBLE_CART_ITEMS);
  }

  function renderCartItems() {
    return visibleCartItems().map(item => (
      <ReverbHeaderCartItem item={item} key={item.product_id} />
    ));
  }

  return (
    <HeaderDropdown
      coreLinkTrigger={<CartBadgeDropdownTrigger countForDataTag={countForDataTag} />}
      disableDropdown={!hasCartItems}
      dropdownKey="cart"
      onOpen={handleDropdownOpen}
      activeDropdownKey={activeDropdownKey}
      hideDropdownOnMobile
      contents={(
        <ul className="reverb-header__menu__cart">
          <ReverbCreditsCallout reverbCredits={reverbCredits} />
          {renderCartItems()}
          <li className="reverb-header__menu__footer">
            <CoreLink
              className="button button--primary width-100"
              to={UrlHelpers.cartPath}
            >
              {I18n.t('discovery.reverbHeader.cart.viewCart')}
            </CoreLink>
          </li>
        </ul>
      )}
    />
  );
}

export default ReverbHeaderCartBadge;
